import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCopy, FaSearch, FaSlidersH, FaTerminal } from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { getCategoryTopics, resolveCommandTopic } from "../_DATA/commandTopics.data";

const LEVELS = [
    { value: "all", label: "Todos" },
    { value: "basic", label: "Básico" },
    { value: "intermediate", label: "Intermedio" },
    { value: "advanced", label: "Avanzado" },
];

const LEVEL_LABELS = {
    basic: "Básico",
    intermediate: "Intermedio",
    advanced: "Avanzado",
};

const copyText = async (value) => {
    try {
        await navigator.clipboard.writeText(String(value ?? ""));
        return true;
    } catch {
        return false;
    }
};

const CommandBlock = ({ item, topic }) => {
    const [copiedCommand, setCopiedCommand] = useState(false);
    const [copiedExample, setCopiedExample] = useState(false);

    const onCopy = async (value, setter) => {
        const ok = await copyText(value);
        if (!ok) return;
        setter(true);
        window.setTimeout(() => setter(false), 1100);
    };

    return (
        <article className="LStudio__commands_entry" data-level={item.level}>
            <div className="LStudio__commands_entry_head">
                <div className="LStudio__commands_entry_title">
                    <div className="LStudio__commands_entry_badges">
                        <span className="LStudio__commands_level" data-level={item.level}>{LEVEL_LABELS[item.level]}</span>
                        {topic && <span className="LStudio__commands_topic_badge">{topic}</span>}
                    </div>
                    <code>{item.command}</code>
                </div>
                <AppButton size="sm" variant="outline" tone="neutral" icon={<FaCopy />}
                    onClick={() => onCopy(item.command, setCopiedCommand)}>
                    {copiedCommand ? "Copiado" : "Copiar"}
                </AppButton>
            </div>

            <p className="LStudio__commands_entry_description">{item.description}</p>

            <div className="LStudio__commands_entry_grid">
                <section>
                    <span className="LStudio__commands_entry_label">Ejemplo</span>
                    <div className="LStudio__commands_code_box">
                        <pre><code>{item.example}</code></pre>
                        <button type="button" aria-label="Copiar ejemplo" onClick={() => onCopy(item.example, setCopiedExample)}>
                            <FaCopy /> {copiedExample ? "Copiado" : "Copiar"}
                        </button>
                    </div>
                </section>

                <section>
                    <span className="LStudio__commands_entry_label">Qué deberías recibir</span>
                    <pre className="LStudio__commands_expected">{item.expected}</pre>
                </section>
            </div>

            {item.note && (
                <div className="LStudio__commands_note"><strong>Nota:</strong> {item.note}</div>
            )}
        </article>
    );
};

export const CommandCategoryModal = ({ category, isOpen, onClose, initialSearch = "" }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState(initialSearch);
    const [level, setLevel] = useState("all");
    const [topic, setTopic] = useState("all");
    const [advancedOpen, setAdvancedOpen] = useState(false);

    const topics = useMemo(() => getCategoryTopics(category), [category]);

    useEffect(() => {
        if (!isOpen) return;
        setLevel(searchParams.get("level") || "all");
        setTopic(searchParams.get("topic") || "all");
        setAdvancedOpen(searchParams.get("filters") === "1");
    }, [isOpen, searchParams]);

    const updateRoute = (patch = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "" || value === "all" || value === false) next.delete(key);
            else next.set(key, value === true ? "1" : String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const onChangeLevel = (value) => { setLevel(value); updateRoute({ level: value }); };
    const onChangeTopic = (value) => { setTopic(value); updateRoute({ topic: value }); };
    const onToggleAdvanced = () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        updateRoute({ filters: next });
    };
    const clearAdvanced = () => {
        setLevel("all"); setTopic("all");
        updateRoute({ level: null, topic: null });
    };

    const visible = useMemo(() => {
        if (!category) return [];
        return category.commands.filter((item) => {
            const resolvedTopic = resolveCommandTopic(category.id, item);
            return (
            (level === "all" || item.level === level)
            && (topic === "all" || resolvedTopic === topic)
            && onMatchesSearch(search, [
                item.command,
                resolvedTopic,
                item.description,
                item.example,
                item.expected,
                item.note,
                category.title,
                category.description,
                ...(category.aliases || []),
            ])
        );
        });
    }, [category, level, search, topic]);

    if (!category) return null;

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="full"
            className="LStudio__commands_modal"
            title={category.title}
            description={`${category.description} ${category.commands.length} comandos documentados.`}>

            <div className="LStudio__commands_modal_toolbar">
                <SearchFilter value={search} onChange={setSearch}
                    total={category.commands.length} matches={visible.length}
                    placeholder={`Buscar dentro de ${category.title}...`} />
                <button
                    type="button"
                    className="LStudio__commands_modal_filter_toggle"
                    data-open={advancedOpen ? "true" : "false"}
                    onClick={onToggleAdvanced}
                    title={(level !== "all" || topic !== "all") ? "Filtros avanzados (activos)" : "Filtros avanzados"}
                    aria-label={(level !== "all" || topic !== "all") ? "Filtros avanzados (activos)" : "Filtros avanzados"}
                >
                    <FaSlidersH />
                </button>
            </div>

            <AdvancedFilterPanel isOpen={advancedOpen} activeCount={Number(level !== "all") + Number(topic !== "all")} onClear={clearAdvanced}>
                <FieldSelect id="commands_modal_level" label="Nivel" value={level}
                    onChange={(event) => onChangeLevel(event.target.value)} options={LEVELS} />
                <FieldSelect id="commands_modal_topic" label="Tema" value={topic}
                    onChange={(event) => onChangeTopic(event.target.value)}
                    options={[{ value: "all", label: "Todos los temas" }, ...topics.map((itemTopic) => ({ value: itemTopic, label: itemTopic }))]} />
            </AdvancedFilterPanel>

            <div className="LStudio__commands_modal_meta">
                <span><FaTerminal /> {visible.length} {visible.length === 1 ? "comando" : "comandos"}</span>
                <span>Comando + descripción + ejemplo + retorno esperado</span>
            </div>

            {visible.length === 0 ? (
                <div className="LStudio__commands_empty">
                    <FaSearch />
                    <strong>No encontramos ese comando</strong>
                    <span>Probá con el nombre, una opción, una tecnología o una palabra de la descripción.</span>
                </div>
            ) : (
                <div className="LStudio__commands_entries">
                    {visible.map((item, index) => <CommandBlock key={`${category.id}-${item.command}-${index}`} item={item}
                        topic={resolveCommandTopic(category.id, item)} />)}
                </div>
            )}
        </AppModal>
    );
};
